import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, CheckCheck } from "lucide-react";

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openNotif = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); }
    setOpen(false);
    if (n.order_id) {
      if (user.role === "seller" || user.is_admin) nav("/seller");
      else nav("/orders");
    } else if (n.type === "listing_pending" && user.is_admin) {
      nav("/admin");
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-[#F3ECE2]" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-[#1F2421]" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#C85A32] text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center" data-testid="notification-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[92vw] bg-white border border-[#E5D9C8] rounded-xl shadow-xl z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5D9C8]">
            <div className="font-extrabold text-[#1E4D3B]">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-[#C85A32] inline-flex items-center gap-1" data-testid="notif-mark-all">
                <CheckCheck className="w-4 h-4" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#5C6660]">You're all caught up.</div>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                className={`w-full text-left px-4 py-3 border-b border-[#F3ECE2] hover:bg-[#FDFBF7] ${!n.read ? "bg-[#FFFBEE]" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-3">
                  {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-[#C85A32] shrink-0" />}
                  <div className="flex-1">
                    <div className="font-bold text-sm text-[#1F2421]">{n.title}</div>
                    <div className="text-xs text-[#5C6660] mt-0.5">{n.body}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8C6D53] mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
